import { markOnboardingPending } from '../utils/onboarding';
import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  IconButton,
  InputAdornment,
  Link,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { authService } from '../services/authService';
import BrandLogo from './BrandLogo';

interface AuthFormProps {
  onSuccess: () => void;
}

export const AuthForm: React.FC<AuthFormProps> = ({ onSuccess }) => {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotView, setForgotView] = useState<'none' | 'form' | 'sent'>('none');

  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const trimmedEmail = email.trim();
      let result;
      if (mode === 'login') {
        result = await authService.login({ email: trimmedEmail, password });
      } else {
        result = await authService.register({ email: trimmedEmail, password, firstName: firstName.trim(), lastName: lastName.trim() });
      }

      if (result.success) {
        // A brand-new account gets the welcome flow (home site, divers to follow, groups) once.
        if (mode === 'register') {
          const newUserId = authService.getUser()?.userId;
          if (newUserId) markOnboardingPending(newUserId);
        }
        onSuccess();
      } else {
        setError(result.error || 'Authentication failed');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = (_event: React.SyntheticEvent, newMode: 'login' | 'register') => {
    setMode(newMode);
    setError(null);
    setForgotView('none');
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await authService.forgotPassword(email.trim());
      if (result.success) {
        setForgotView('sent');
      } else {
        setError(result.error || 'Request failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="flex-start"
      sx={{ padding: 2 }}
    >
      <Paper
        elevation={6}
        sx={{
          padding: { xs: 3, sm: 4 },
          maxWidth: 450,
          width: '100%',
          borderRadius: 4,
          border: '1px solid rgba(17, 191, 174, 0.18)',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(244,252,255,0.94) 100%)',
          boxShadow: '0 24px 60px rgba(5, 27, 42, 0.22)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <BrandLogo
            size={70}
            stacked
            textColor="#08263A"
            accentColor="#0EAFA0"
            subtitle="Where divers connect"
          />
        </Box>

        <Typography variant="body2" align="center" sx={{ color: '#416378', mb: 2.5 }}>
          Share your dives, find buddies, join clubs and groups, and plan trips together — with your dive log and planning tools built right in.
        </Typography>

        {forgotView === 'sent' && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              If an account exists for <strong>{email.trim()}</strong>, a password
              reset link is on its way. The link expires in 1 hour.
            </Alert>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setForgotView('none');
                setError(null);
              }}
            >
              Back to Login
            </Button>
          </>
        )}

        {forgotView === 'form' && (
          <form onSubmit={handleForgotSubmit}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Reset your password
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Enter your account email and we'll send you a reset link.
            </Typography>
            <TextField
              label="Email"
              type="email"
              name="email"
              autoComplete="email"
              autoFocus
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ mt: 2, py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
            </Button>
            <Button
              fullWidth
              size="small"
              sx={{ mt: 1.5, textTransform: 'none', color: 'text.secondary' }}
              onClick={() => {
                setForgotView('none');
                setError(null);
              }}
            >
              Back to Login
            </Button>
          </form>
        )}

        {forgotView === 'none' && (
        <>
        <Tabs
          value={mode}
          onChange={handleModeChange}
          variant="fullWidth"
          sx={{ mb: 3 }}
        >
          <Tab label="Login" value="login" />
          <Tab label="Register" value="register" />
        </Tabs>

        <form onSubmit={handleSubmit}>
          {mode === 'register' && (
            <>
              <TextField
                label="First Name"
                name="firstName"
                autoComplete="given-name"
                fullWidth
                margin="normal"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
              <TextField
                label="Last Name"
                name="lastName"
                autoComplete="family-name"
                fullWidth
                margin="normal"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </>
          )}

          <TextField
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            helperText={mode === 'register' ? 'Minimum 8 characters' : ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((show) => !show)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={loading}
            sx={{
              mt: 3,
              py: 1.5,
              background: 'linear-gradient(135deg, #08324A 0%, #0C5F7A 56%, #10AFA5 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0A3D5A 0%, #0E6D88 56%, #12B9A8 100%)',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : mode === 'login' ? (
              'Login'
            ) : (
              'Create Account'
            )}
          </Button>

          {mode === 'login' && (
            <Typography variant="body2" align="center" sx={{ mt: 1.5 }}>
              <Button
                variant="text"
                size="small"
                sx={{ color: 'text.secondary', textTransform: 'none', p: 0, minWidth: 0 }}
                onClick={() => {
                  setForgotView('form');
                  setError(null);
                }}
              >
                Forgot password?
              </Button>
            </Typography>
          )}
        </form>

        <Typography
          variant="body2"
          align="center"
          sx={{ mt: 3, color: 'text.secondary' }}
        >
          {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() => {
              setMode(mode === 'login' ? 'register' : 'login');
              setError(null);
            }}
          >
            {mode === 'login' ? 'Create one' : 'Log in'}
          </Link>
        </Typography>
        </>
        )}
      </Paper>
    </Box>
  );
};
